// Firestore Collection Configuration and Data Structure
// This file defines the structure of inspectionRun collection based on RunMetadata schema

//[TODO] right now its getting everythign which is fine for small number of objects
//but once the number of runs increase we should to pagination
export const INSPECTIONRUN_COLLECTION_NAME = 'inspectionRun';

/**
 * Encode a string to base64, handling Unicode characters safely.
 * The standard btoa() only supports Latin1 (0-255), so we first encode to UTF-8.
 * @param {string} str - The string to encode
 * @returns {string} - Base64 encoded string
 */
function safeBase64Encode(str) {
  try {
    // First try standard btoa for ASCII-only strings (faster)
    return btoa(str);
  } catch (e) {
    // For Unicode strings, encode to UTF-8 first
    try {
      const utf8Bytes = new TextEncoder().encode(str);
      let binary = '';
      for (let i = 0; i < utf8Bytes.length; i++) {
        binary += String.fromCharCode(utf8Bytes[i]);
      }
      return btoa(binary);
    } catch (e2) {
      console.warn('Failed to base64 encode string:', e2);
      // Fallback: return URL-encoded version as data URI
      return null;
    }
  }
}

export function extractDateFromWatchFile(filename) {
  if (!filename) return null;

  const str = String(filename);

  const toUtcDate = (year, month1, day, hour, minute, second) =>
    new Date(Date.UTC(year, month1 - 1, day, hour, minute, second));

  const parse12 = (dateStr) => {
    // YYMMDDHHMMSS
    const year = 2000 + Number.parseInt(dateStr.slice(0, 2), 10);
    const month = Number.parseInt(dateStr.slice(2, 4), 10);
    const day = Number.parseInt(dateStr.slice(4, 6), 10);
    const hour = Number.parseInt(dateStr.slice(6, 8), 10);
    const minute = Number.parseInt(dateStr.slice(8, 10), 10);
    const second = Number.parseInt(dateStr.slice(10, 12), 10);
    return toUtcDate(year, month, day, hour, minute, second);
  };

  const parse14 = (dateStr) => {
    // YYYYMMDDHHMMSS
    const year = Number.parseInt(dateStr.slice(0, 4), 10);
    const month = Number.parseInt(dateStr.slice(4, 6), 10);
    const day = Number.parseInt(dateStr.slice(6, 8), 10);
    const hour = Number.parseInt(dateStr.slice(8, 10), 10);
    const minute = Number.parseInt(dateStr.slice(10, 12), 10);
    const second = Number.parseInt(dateStr.slice(12, 14), 10);
    return toUtcDate(year, month, day, hour, minute, second);
  };

  // Default: watch_YYMMDDHHMMSS_...
  let m12 = /watch_(\d{12})_/.exec(str);
  if (m12) return parse12(m12[1]);

  // autowatch fallback
  m12 = /watch_(\d{12})/.exec(str);
  if (m12) return parse12(m12[1]);

  // Fallback: autowatchdir_YYYYMMDDHHMMSS / watchdir_YYYYMMDDHHMMSS / autowatch_YYYYMMDDHHMMSS
  const m14 = /(?:autowatchdir|watchdir|autowatch)_(\d{14})(?:_|$)/.exec(str);
  if (m14) return parse14(m14[1]);

  return null;
}

const SIZE_UNITS = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 };

function parseLogSize(value) {
  if (typeof value === 'number' && isFinite(value)) return value;
  if (typeof value !== 'string' || !value) return null;
  const cleaned = value.trim().replace(/,/g, '');
  const match = cleaned.match(/^([\d.]+)\s*(B|KB|MB|GB|TB)?$/i);
  if (!match) return null;
  const num = parseFloat(match[1]);
  if (!isFinite(num) || num < 0) return null;
  const unit = (match[2] || 'B').toUpperCase();
  return Math.round(num * (SIZE_UNITS[unit] || 1));
}

/**
 * RunMetadata class that matches the C# RunMetadata schema
 * Directly converts Firebase Firestore document to RunMetadata object
 */
export class RunMetadata {
  constructor(doc) {
    if (doc && doc.data) {
      // From Firestore document
      const data = doc.data();
      this.id = doc.id || '';
      this.app = data.App || '';
      this.version = data.Version || '';
      this.watchDir = data.WatchDir || '';
      this.blobPath = data.BlobPath || '';
      this.notes = data.Notes || '';
      this.uploadDate = data.UploadDate || '';
      this.status = data.Status || '';
      this.uploadedBy = data.UploadedBy || '';
      this.executable = data.Executable || '';
      this.pids = data.Pids || [];
      this.filters = data.Filters || [];
      this.copyExeMapping = data.CopyExeMapping || {};
      this.logSize = data.LogSize ? parseLogSize(data.LogSize) : null;
      this.isTest = data.isTest || false;
      // "windows" | "macos" — written by aebuild upload from metadata.json since dd336fe
      // (2026-08-29). Absent on older docs: '' means "unknown, read metadata.json when
      // the run is opened", NOT Windows.
      this.platform = typeof data.Platform === 'string' ? data.Platform.trim().toLowerCase() : '';
      this.platformVersion = typeof data.PlatformVersion === 'string' ? data.PlatformVersion : '';
    } else {
      // Default constructor
      this.id = '';
      this.app = '';
      this.version = '';
      this.watchDir = '';
      this.blobPath = '';
      this.notes = '';
      this.uploadDate = '';
      this.status = '';
      this.uploadedBy = '';
      this.executable = '';
      this.pids = [];
      this.filters = [];
      this.copyExeMapping = {};
      this.logSize = null;
      this.isTest = false;
      this.platform = '';
      this.platformVersion = '';
    }
  }

  /**
   * Convert RunMetadata to the format expected by the UI components
   */
  toAppInfo(appName, version, inspection) {
    // Safely encode notes to base64 data URI, handling Unicode characters
    let notesDataUri = null;
    if (this.notes) {
      const encoded = safeBase64Encode(this.notes);
      if (encoded) {
        notesDataUri = `data:text/plain;base64,${encoded}`;
      } else {
        // Fallback to URL-encoded data URI if base64 fails
        notesDataUri = `data:text/plain,${encodeURIComponent(this.notes)}`;
      }
    }
    
    return {
      appName: appName || this.app,
      version: version || this.version,
      inspection: inspection || this.watchDir,
      uploadDate: this.uploadDate,
      status: this.status,
      uploadedBy: this.uploadedBy,
      executable: this.executable,
      pids: this.pids,
      filters: this.filters,
      copyExeMapping: this.copyExeMapping,
      notes: notesDataUri,
      logSize: this.logSize || null,
      isTest: this.isTest,
      platform: this.platform,
      platformVersion: this.platformVersion
    };
  }

  /**
   * Convert RunMetadata to the format expected by the table view
   */
  toTableRow() {
    // Extract date from watch file name (matching base file behavior)
    const extractedDate = extractDateFromWatchFile(this.watchDir);
    
    return {
      id: this.id,
      app: this.app,
      version: this.version,
      inspection: this.watchDir,
      date: extractedDate,
      dateString: extractedDate ? extractedDate.toLocaleString() : 'N/A',
      uploadDate: this.uploadDate,
      status: this.status,
      uploadedBy: this.uploadedBy,
      notes: this.notes,
      blobPath: this.blobPath,
      logSize: this.logSize,
      isTest: this.isTest,
      platform: this.platform,
      platformVersion: this.platformVersion
    };
  }

  /**
   * Convert RunMetadata to the format expected by the hierarchy list
   */
  toHierarchyItem() {
    return {
      id: this.id,
      watchDir: this.watchDir,
      uploadDate: this.uploadDate,
      status: this.status,
      notes: this.notes,
      uploadedBy: this.uploadedBy
    };
  }

  /**
   * Check if RunMetadata has all required fields for display
   */
  isValid() {
    return !!(this.app && this.version && this.watchDir);
  }

  /**
   * Get the inspection key used in the hierarchy
   */
  getInspectionKey() {
    return `${this.app}/${this.version}/${this.watchDir}`;
  }
}

/**
 * Helper function to convert Firestore document to RunMetadata
 * @param {DocumentSnapshot} doc - Firestore document snapshot
 * @returns {RunMetadata} - RunMetadata instance
 */
export const createRunMetadataFromDoc = (doc) => {
  return new RunMetadata(doc);
};

/**
 * Helper function to convert multiple Firestore documents to RunMetadata array
 * @param {QuerySnapshot} querySnapshot - Firestore query snapshot
 * @returns {RunMetadata[]} - Array of RunMetadata instances
 */
export const createRunMetadataArrayFromQuery = (querySnapshot) => {
  const runMetadataArray = [];
  querySnapshot.forEach((doc) => {
    const runMetadata = new RunMetadata(doc);
    if (runMetadata.isValid()) {
      runMetadataArray.push(runMetadata);
    }
  });
  return runMetadataArray;
};
